import { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';

interface SessionTimerConfig {
  sessionDuration: number; // in minutes
  warningTime: number; // in minutes before expiry to show warning
  onSessionExpired: () => void;
  onSessionWarning?: (remainingTime: number) => void;
}

interface SessionState {
  remainingTime: number; // in seconds
  isExpired: boolean;
  isWarning: boolean;
  lastActivity: number;
}

export const useSessionTimer = (config: SessionTimerConfig) => {
  const [sessionState, setSessionState] = useState<SessionState>({
    remainingTime: config.sessionDuration * 60,
    isExpired: false,
    isWarning: false,
    lastActivity: Date.now()
  });

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const warningShownRef = useRef(false);
  const renewalRequestRef = useRef<Promise<any> | null>(null);

  // Reset session timer
  const resetSession = useCallback(() => {
    setSessionState(prev => ({
      ...prev,
      remainingTime: config.sessionDuration * 60,
      isExpired: false,
      isWarning: false,
      lastActivity: Date.now()
    }));
    warningShownRef.current = false;
  }, [config.sessionDuration]);

  // Update last activity
  const updateLastActivity = useCallback(() => {
    setSessionState(prev => ({
      ...prev,
      lastActivity: Date.now()
    }));
  }, []);

  // Extend session by calling refresh token API
  const extendSession = useCallback(async () => {
    try {
      // Prevent multiple simultaneous renewal requests
      if (renewalRequestRef.current) {
        return await renewalRequestRef.current;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found');
      }

      // Create renewal request using the API service
      renewalRequestRef.current = apiService.refreshToken();

      const result = await renewalRequestRef.current;
      
      if (result?.token) {
        // Reset session timer
        resetSession();
        
        toast.success('세션이 연장되었습니다!', {
          duration: 2000,
          icon: '⏰'
        });
        
        return true;
      } else {
        throw new Error('Failed to refresh token');
      }
    } catch (error) {
      console.error('Failed to extend session:', error);
      toast.error('세션 연장에 실패했습니다.');
      return false;
    } finally {
      renewalRequestRef.current = null;
    }
  }, [resetSession]);

  // Format time display
  const formatTime = useCallback((seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}분 ${remainingSeconds}초`;
    } else {
      return `${remainingSeconds}초`;
    }
  }, []);

  // Start timer
  const startTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      setSessionState(prev => {
        const newRemainingTime = prev.remainingTime - 1;
        
        // Check if session expired
        if (newRemainingTime <= 0) {
          clearInterval(intervalRef.current!);
          config.onSessionExpired();
          return {
            ...prev,
            remainingTime: 0,
            isExpired: true,
            isWarning: false
          };
        }
        
        // Check if we should show warning
        const warningThreshold = config.warningTime * 60;
        const shouldShowWarning = newRemainingTime <= warningThreshold;
        
        if (shouldShowWarning && !warningShownRef.current) {
          warningShownRef.current = true;
          config.onSessionWarning?.(newRemainingTime);
          
          // Show simple toast warning
          toast(
            `⚠️ 세션 만료 경고: ${Math.ceil(newRemainingTime / 60)}분 후 자동 로그아웃됩니다.`,
            {
              duration: 10000,
              icon: '⚠️',
              style: {
                background: '#fef3c7',
                color: '#92400e',
                border: '1px solid #f59e0b',
              },
            }
          );
        }
        
        return {
          ...prev,
          remainingTime: newRemainingTime,
          isWarning: shouldShowWarning
        };
      });
    }, 1000);
  }, [config, extendSession]);

  // Stop timer
  const stopTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Initialize timer on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      startTimer();
    }

    return () => {
      stopTimer();
    };
  }, [startTimer, stopTimer]);

  // Listen for user activity to reset inactivity timer (optional feature)
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      updateLastActivity();
    };

    // Add event listeners for user activity
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [updateLastActivity]);

  return {
    remainingTime: sessionState.remainingTime,
    isExpired: sessionState.isExpired,
    isWarning: sessionState.isWarning,
    formattedTime: formatTime(sessionState.remainingTime),
    extendSession,
    resetSession,
    startTimer,
    stopTimer
  };
};